import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, Lock, Eye, UserCheck, Database, Mail, Smartphone } from "lucide-react";
import i18n from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import Seo from "../components/Seo";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(i18n.language?.split("-")[0] || "fr");

  const changeLang = (lang) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang);
  };

  const t = (obj) => obj[currentLang] || obj.fr;

  return (
    <div
      className="dark min-h-screen"
      style={{ backgroundColor: "var(--lm-canvas)", fontFamily: "Inter, system-ui, sans-serif" }}
    >
      <Seo
        path="/privacy"
        title="Politique de Confidentialité — Mawered"
        description="Politique de confidentialité de Mawered (Teknovat) : traitement des données RH, sécurité, droits des utilisateurs et conformité."
      />
      {/* Header */}
      <div style={{ backgroundColor: "var(--lm-s1)", borderBottom: "1px solid var(--lm-border)" }}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate("/")} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t({ fr: "Retour", en: "Back", ar: "عودة" })}
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                {t({
                  fr: "Politique de Confidentialité",
                  en: "Privacy Policy",
                  ar: "سياسة الخصوصية",
                })}
              </h1>
              <p className="text-muted-foreground">
                {t({
                  fr: "Dernière mise à jour : 24 juillet 2026",
                  en: "Last updated: July 24, 2026",
                  ar: "آخر تحديث: 24 يوليو 2026",
                })}
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant={currentLang === "fr" ? "default" : "outline"} size="sm" onClick={() => changeLang("fr")}>
                FR
              </Button>
              <Button variant={currentLang === "en" ? "default" : "outline"} size="sm" onClick={() => changeLang("en")}>
                EN
              </Button>
              <Button variant={currentLang === "ar" ? "default" : "outline"} size="sm" onClick={() => changeLang("ar")}>
                AR
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="space-y-6">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "Notre Engagement",
                    en: "Our Commitment",
                    ar: "التزامنا",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                {t({
                  fr: "Chez Mawered, nous prenons la protection de vos données personnelles très au sérieux. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations conformément au Code de la Protection des Données Personnelles tunisien et au RGPD européen.",
                  en: "At Mawered, we take the protection of your personal data very seriously. This privacy policy explains how we collect, use, store and protect your information in accordance with the Tunisian Personal Data Protection Code and the European GDPR.",
                  ar: "في Mawered، نأخذ حماية بياناتك الشخصية على محمل الجد. توضح سياسة الخصوصية هذه كيفية جمع معلوماتك واستخدامها وتخزينها وحمايتها وفقًا لقانون حماية البيانات الشخصية التونسي واللائحة العامة لحماية البيانات الأوروبية.",
                })}
              </p>
            </CardContent>
          </Card>

          {/* Data Collection */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Database className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "1. Données Collectées",
                    en: "1. Data Collected",
                    ar: "1. البيانات المجمعة",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t({
                  fr: "Nous collectons les informations suivantes :",
                  en: "We collect the following information:",
                  ar: "نقوم بجمع المعلومات التالية:",
                })}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  {t({
                    fr: "Informations d'identification : nom, prénom, email, téléphone",
                    en: "Identification information: name, surname, email, phone",
                    ar: "معلومات التعريف: الاسم، اللقب، البريد الإلكتروني، الهاتف",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Informations professionnelles : entreprise, poste, secteur d'activité",
                    en: "Professional information: company, position, industry",
                    ar: "المعلومات المهنية: الشركة، المنصب، القطاع",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Données d'utilisation : logs de connexion, pages visitées, fonctionnalités utilisées",
                    en: "Usage data: connection logs, pages visited, features used",
                    ar: "بيانات الاستخدام: سجلات الاتصال، الصفحات المزارة، الميزات المستخدمة",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Données RH (pour nos clients) : informations employés selon configuration",
                    en: "HR data (for our clients): employee information as configured",
                    ar: "بيانات الموارد البشرية (لعملائنا): معلومات الموظفين حسب التكوين",
                  })}
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Usage */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "2. Utilisation des Données",
                    en: "2. Data Usage",
                    ar: "2. استخدام البيانات",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t({
                  fr: "Vos données sont utilisées uniquement pour :",
                  en: "Your data is used only for:",
                  ar: "تُستخدم بياناتك فقط من أجل:",
                })}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  {t({
                    fr: "Fournir et améliorer nos services HR",
                    en: "Provide and improve our HR services",
                    ar: "توفير وتحسين خدمات الموارد البشرية لدينا",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Communiquer avec vous (support, mises à jour, facturation)",
                    en: "Communicate with you (support, updates, billing)",
                    ar: "التواصل معك (الدعم، التحديثات، الفوترة)",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Respecter nos obligations légales et réglementaires",
                    en: "Comply with our legal and regulatory obligations",
                    ar: "الامتثال لالتزاماتنا القانونية والتنظيمية",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Analyser l'utilisation pour améliorer notre plateforme",
                    en: "Analyze usage to improve our platform",
                    ar: "تحليل الاستخدام لتحسين منصتنا",
                  })}
                </li>
              </ul>
              <div className="bg-muted/30 border border-border rounded-lg p-4 mt-4">
                <p className="text-sm text-foreground font-semibold">
                  {t({
                    fr: "Nous ne vendons JAMAIS vos données à des tiers.",
                    en: "We NEVER sell your data to third parties.",
                    ar: "نحن لا نبيع بياناتك أبدًا لأطراف ثالثة.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Protection */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "3. Protection des Données",
                    en: "3. Data Protection",
                    ar: "3. حماية البيانات",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t({
                  fr: "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles :",
                  en: "We implement technical and organizational security measures:",
                  ar: "ننفذ إجراءات أمنية تقنية وتنظيمية:",
                })}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-border rounded-lg p-4">
                  <Badge className="mb-2 bg-green-950/30 text-green-400">
                    {t({ fr: "Chiffrement", en: "Encryption", ar: "التشفير" })}
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {t({
                      fr: "Chiffrement AES-256 pour les données au repos et en transit (SSL/TLS)",
                      en: "AES-256 encryption for data at rest and in transit (SSL/TLS)",
                      ar: "تشفير AES-256 للبيانات الثابتة والمنقولة (SSL/TLS)",
                    })}
                  </p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <Badge className="mb-2 bg-green-950/30 text-green-400">
                    {t({ fr: "Accès Contrôlé", en: "Controlled Access", ar: "وصول محكوم" })}
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {t({
                      fr: "Authentification à deux facteurs et gestion des rôles stricte",
                      en: "Two-factor authentication and strict role management",
                      ar: "مصادقة ثنائية العوامل وإدارة الأدوار الصارمة",
                    })}
                  </p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <Badge className="mb-2 bg-green-950/30 text-green-400">
                    {t({ fr: "Sauvegardes", en: "Backups", ar: "النسخ الاحتياطية" })}
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {t({
                      fr: "Sauvegardes quotidiennes chiffrées avec rétention de 30 jours",
                      en: "Daily encrypted backups with 30-day retention",
                      ar: "نسخ احتياطية يومية مشفرة بفترة احتفاظ 30 يومًا",
                    })}
                  </p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <Badge className="mb-2 bg-green-950/30 text-green-400">
                    {t({ fr: "Hébergement", en: "Hosting", ar: "الاستضافة" })}
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {t({
                      fr: "Serveurs sécurisés en Tunisie conformes aux normes locales",
                      en: "Secure servers in Tunisia compliant with local standards",
                      ar: "خوادم آمنة في تونس متوافقة مع المعايير المحلية",
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "4. Vos Droits",
                    en: "4. Your Rights",
                    ar: "4. حقوقك",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t({
                  fr: "Conformément au RGPD et à la loi tunisienne, vous disposez des droits suivants :",
                  en: "In accordance with GDPR and Tunisian law, you have the following rights:",
                  ar: "وفقًا للائحة العامة لحماية البيانات والقانون التونسي، لديك الحقوق التالية:",
                })}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  {t({
                    fr: "Droit d'accès : consulter vos données personnelles",
                    en: "Right of access: view your personal data",
                    ar: "حق الوصول: عرض بياناتك الشخصية",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Droit de rectification : corriger vos données inexactes",
                    en: "Right to rectification: correct inaccurate data",
                    ar: "حق التصحيح: تصحيح البيانات غير الدقيقة",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Droit à l'effacement : supprimer vos données (droit à l'oubli)",
                    en: "Right to erasure: delete your data (right to be forgotten)",
                    ar: "حق المحو: حذف بياناتك (الحق في النسيان)",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Droit à la portabilité : récupérer vos données dans un format standard",
                    en: "Right to portability: retrieve your data in a standard format",
                    ar: "حق النقل: استرداد بياناتك بتنسيق قياسي",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Droit d'opposition : refuser certains traitements de vos données",
                    en: "Right to object: refuse certain processing of your data",
                    ar: "حق الاعتراض: رفض معالجة معينة لبياناتك",
                  })}
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Mobile Application */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Smartphone className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "5. Application Mobile",
                    en: "5. Mobile Application",
                    ar: "5. تطبيق الهاتف المحمول",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t({
                  fr: "L'application mobile Mawered utilise certaines fonctionnalités de votre appareil, exclusivement pour les usages suivants :",
                  en: "The Mawered mobile app uses certain features of your device, exclusively for the following purposes:",
                  ar: "يستخدم تطبيق Mawered للهاتف المحمول بعض ميزات جهازك، حصريًا للأغراض التالية:",
                })}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  {t({
                    fr: "Biométrie (empreinte / reconnaissance faciale) : utilisée uniquement pour déverrouiller l'application sur votre propre appareil (« App Lock »), si vous activez cette option. La donnée biométrique est traitée par le système d'exploitation de votre téléphone et n'est jamais transmise à Mawered ni stockée sur nos serveurs.",
                    en: "Biometrics (fingerprint / face recognition): used only to unlock the app on your own device (\"App Lock\"), if you enable this option. Biometric data is processed by your phone's operating system and is never transmitted to Mawered or stored on our servers.",
                    ar: "القياسات الحيوية (بصمة الإصبع / التعرف على الوجه): تُستخدم فقط لإلغاء قفل التطبيق على جهازك الخاص (\"قفل التطبيق\")، إذا فعّلت هذا الخيار. تتم معالجة البيانات الحيوية بواسطة نظام تشغيل هاتفك ولا تُرسل أبدًا إلى Mawered ولا تُخزَّن على خوادمنا.",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Caméra : utilisée uniquement pour scanner le QR code de connexion à votre organisation lors de la première configuration. Aucune photo n'est prise, stockée ou transmise.",
                    en: "Camera: used only to scan the QR code that connects the app to your organization during first-time setup. No photo is taken, stored or transmitted.",
                    ar: "الكاميرا: تُستخدم فقط لمسح رمز QR للاتصال بمؤسستك عند الإعداد الأول. لا يتم التقاط أي صورة أو تخزينها أو إرسالها.",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Géolocalisation : collectée uniquement au moment où vous pointez (arrivée/départ) depuis l'application, afin d'horodater et géolocaliser votre pointage pour le compte de votre employeur. Non collectée en arrière-plan ni en dehors de cette action.",
                    en: "Location: collected only at the moment you clock in or out from the app, to timestamp and geolocate your punch on behalf of your employer. Not collected in the background or outside of that action.",
                    ar: "الموقع الجغرافي: يُجمع فقط عند تسجيل الحضور أو الانصراف من التطبيق، لتوقيت وتحديد موقع تسجيلك لحساب صاحب العمل. لا يُجمع في الخلفية أو خارج هذا الإجراء.",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Notifications push : un identifiant d'appareil est enregistré pour vous envoyer des notifications (validations, rappels). Vous pouvez désactiver les notifications dans les paramètres de votre téléphone.",
                    en: "Push notifications: a device identifier is registered to send you notifications (approvals, reminders). You can disable notifications in your phone's settings.",
                    ar: "الإشعارات الفورية: يُسجَّل معرّف الجهاز لإرسال الإشعارات إليك (الموافقات، التذكيرات). يمكنك تعطيل الإشعارات من إعدادات هاتفك.",
                  })}
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "6. Nous Contacter",
                    en: "6. Contact Us",
                    ar: "6. اتصل بنا",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t({
                  fr: "Pour toute question concernant cette politique ou pour exercer vos droits :",
                  en: "For any questions about this policy or to exercise your rights:",
                  ar: "لأي أسئلة حول هذه السياسة أو لممارسة حقوقك:",
                })}
              </p>
              <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
                <p className="text-muted-foreground">
                  <strong>Email:</strong> support@mawered.com
                </p>
                <p className="text-muted-foreground">
                  <strong>{t({ fr: "Adresse", en: "Address", ar: "العنوان" })}:</strong> Mawered SARL, Tunis, Tunisia
                </p>
                <p className="text-muted-foreground">
                  <strong>{t({ fr: "Délai de réponse", en: "Response time", ar: "وقت الاستجابة" })}:</strong>{" "}
                  {t({
                    fr: "30 jours maximum",
                    en: "Maximum 30 days",
                    ar: "30 يومًا كحد أقصى",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Footer */}
          <div className="text-center text-muted-foreground text-sm">
            <p>
              {t({
                fr: "Cette politique peut être mise à jour. La dernière version est toujours disponible sur cette page.",
                en: "This policy may be updated. The latest version is always available on this page.",
                ar: "قد يتم تحديث هذه السياسة. النسخة الأحدث متاحة دائمًا على هذه الصفحة.",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
